import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { Request, RequestFile } from '../../models/request.model';
import { isPrintableImageUrl } from '../../utils/request-file.util';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';

interface PrintSection {
    key: string;
    label: string;
    group: 'citizen' | 'internal' | 'photos';
    selected: boolean;
    disabled: boolean;
}

export interface PrintPhoto {
    url: string;
    name: string;
    source: 'citizen' | 'internal';
    selected: boolean;
}

export interface PrintPhotoGroup {
    label: string;
    photos: PrintPhoto[];
}

export interface RequestPrintDialogData {
    request: Request;
}

@Component({
    selector: 'app-request-print-dialog',
    templateUrl: './request-print-dialog.component.html',
    styleUrls: ['./request-print-dialog.component.scss'],
    providers: [DatePipe],
})
export class RequestPrintDialogComponent implements OnInit {
    sections: PrintSection[];
    photos: PrintPhoto[];
    photoGroups: PrintPhotoGroup[];
    private requesterPhone = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RequestPrintDialogData,
        private readonly dialogRef: MatDialogRef<RequestPrintDialogComponent>,
        private translate: TranslateService,
        private datePipe: DatePipe,
        private userService: UserService,
    ) {
        const req = data.request;
        const hasValidLocation = !!(req.location?.geometry?.coordinates?.length >= 2 || req.location?.address?.formattedAddress);
        this.photos = [...this.toPrintPhotos(req.files, 'citizen'), ...this.toPrintPhotos(req.internalFiles, 'internal')];
        this.photoGroups = [
            { label: 'request_v2.print.photos.citizen', photos: this.citizenPhotos },
            { label: 'request_v2.print.photos.internal', photos: this.internalPhotos },
        ];

        this.sections = [
            {
                key: 'info',
                label: this.t('request_v2.print.section.info'),
                group: 'citizen',
                selected: true,
                disabled: false,
            },
            {
                key: 'requester',
                label: this.t('request_v2.print.section.requester'),
                group: 'citizen',
                selected: !!req.user,
                disabled: !req.user,
            },
            {
                key: 'description',
                label: this.t('request_v2.print.section.description'),
                group: 'citizen',
                selected: !!req.description,
                disabled: !req.description,
            },
            {
                key: 'address',
                label: this.t('request_v2.print.section.address'),
                group: 'citizen',
                selected: hasValidLocation,
                disabled: !hasValidLocation,
            },
            {
                key: 'map',
                label: this.t('request_v2.print.section.map'),
                group: 'citizen',
                selected: req.location?.geometry?.coordinates?.length >= 2,
                disabled: !(req.location?.geometry?.coordinates?.length >= 2),
            },
            {
                key: 'messages',
                label: this.t('request_v2.print.section.messages'),
                group: 'citizen',
                selected: req.messages?.some((m) => !m.deleted) ?? false,
                disabled: !(req.messages?.some((m) => !m.deleted) ?? false),
            },
            {
                key: 'tasks',
                label: this.t('request_v2.print.section.tasks'),
                group: 'internal',
                selected: !!req.subtasks?.length,
                disabled: !req.subtasks?.length,
            },
            {
                key: 'linked',
                label: this.t('request_v2.print.section.linked'),
                group: 'internal',
                selected: !!req.links?.length,
                disabled: !req.links?.length,
            },
            {
                key: 'tags',
                label: this.t('request_v2.print.section.tags'),
                group: 'internal',
                selected: !!req.tags?.length,
                disabled: !req.tags?.length,
            },
            {
                key: 'internal_files',
                label: this.t('request_v2.print.section.internal_files'),
                group: 'internal',
                selected: !!req.internalFiles?.length,
                disabled: !req.internalFiles?.length,
            },
            {
                key: 'internal_description',
                label: this.t('request_v2.print.section.internal_description'),
                group: 'internal',
                selected: !!req.internalDescription,
                disabled: !req.internalDescription,
            },
            {
                key: 'notes',
                label: this.t('request_v2.print.section.notes'),
                group: 'internal',
                selected: !!req.internalNotes?.length,
                disabled: !req.internalNotes?.length,
            },
            {
                key: 'history',
                label: this.t('request_v2.print.section.history'),
                group: 'internal',
                selected: !!req.history?.length,
                disabled: !req.history?.length,
            },
            {
                key: 'photos',
                label: this.t('request_v2.print.section.photos'),
                group: 'photos',
                selected: this.photos.length > 0,
                disabled: this.photos.length === 0,
            },
        ];
    }

    ngOnInit(): void {
        const userId = this.data.request?.user?._id;
        if (!userId) return;
        this.userService
            .getProfileById(userId)
            .pipe(take(1))
            .subscribe({
                next: (profile) => {
                    this.requesterPhone = profile?.cellPhone || profile?.phone || '';
                },
                error: () => {},
            });
    }

    get citizenSections(): PrintSection[] {
        return this.sections.filter((s) => s.group === 'citizen');
    }

    get internalSections(): PrintSection[] {
        return this.sections.filter((s) => s.group === 'internal');
    }

    get photosSection(): PrintSection {
        return this.sections.find((s) => s.key === 'photos');
    }

    get citizenPhotos(): PrintPhoto[] {
        return this.photos.filter((p) => p.source === 'citizen');
    }

    get internalPhotos(): PrintPhoto[] {
        return this.photos.filter((p) => p.source === 'internal');
    }

    get selectedPhotos(): PrintPhoto[] {
        return this.photos.filter((p) => p.selected);
    }

    get selectedCount(): number {
        return this.sections.filter((s) => s.selected).length;
    }

    toggleByKey(key: string): void {
        const section = this.sections.find((s) => s.key === key);
        if (section && !section.disabled) {
            section.selected = !section.selected;
            // Turning photos back on after every photo was unchecked would print nothing
            if (key === 'photos' && section.selected && !this.selectedPhotos.length) {
                this.photos.forEach((p) => (p.selected = true));
            }
        }
    }

    togglePhoto(photo: PrintPhoto): void {
        photo.selected = !photo.selected;
        this.photosSection.selected = this.selectedPhotos.length > 0;
    }

    isGroupSelected(group: PrintPhotoGroup): boolean {
        return group.photos.every((p) => p.selected);
    }

    isGroupPartlySelected(group: PrintPhotoGroup): boolean {
        return !this.isGroupSelected(group) && group.photos.some((p) => p.selected);
    }

    togglePhotoGroup(group: PrintPhotoGroup): void {
        const selected = !this.isGroupSelected(group);
        group.photos.forEach((p) => (p.selected = selected));
        this.photosSection.selected = this.selectedPhotos.length > 0;
    }

    print(): void {
        const selected = new Set(this.sections.filter((s) => s.selected).map((s) => s.key));
        this.openPrintWindow(selected);
        this.dialogRef.close();
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private toPrintPhotos(files: RequestFile[], source: PrintPhoto['source']): PrintPhoto[] {
        return (files || []).filter((f) => isPrintableImageUrl(f?.url)).map((f) => ({ url: f.url, name: f.originalName || this.fileNameFromUrl(f.url), source, selected: true }));
    }

    private fileNameFromUrl(url: string): string {
        const path = url.split('?')[0];
        return decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
    }

    private t(key: string): string {
        return this.translate.instant(key);
    }

    private formatDate(date: Date | string): string {
        if (!date) return '';
        const locale = this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA';
        return this.datePipe.transform(date, 'd MMM yyyy', undefined, locale) || '';
    }

    private formatDateTime(date: Date | string): string {
        if (!date) return '';
        const locale = this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA';
        return this.datePipe.transform(date, 'd MMM yyyy HH:mm', undefined, locale) || '';
    }

    private esc(str: string): string {
        if (!str) return '';
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    private nl2br(str: string): string {
        return this.esc(str).replace(/\n/g, '<br>');
    }

    private getInitials(firstName: string, lastName: string): string {
        const f = (firstName || '').charAt(0).toUpperCase();
        const l = (lastName || '').charAt(0).toUpperCase();
        return f + l;
    }

    private formatAddressLine1(location: any): string {
        if (!location?.address) return '';
        const addr = location.address;
        if (typeof addr === 'string') return addr;
        const parts: string[] = [];
        if (addr.unit) parts.push(`App. ${addr.unit}`);
        if (addr.number) parts.push(String(addr.number));
        if (addr.streetName) parts.push(addr.streetName);
        return parts.join(' ');
    }

    private formatAddressLine2(location: any): string {
        if (!location?.address) return '';
        const addr = location.address;
        if (typeof addr === 'string') return '';
        const city = addr.city ? String(addr.city).trim() : '';
        const raw = String(addr.postCode || '')
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase();
        const postal = raw.length === 6 ? `${raw.slice(0, 3)} ${raw.slice(3)}` : addr.postCode || '';
        if (city && postal) return `${city}, ${postal}`;
        return city || String(postal);
    }

    private openPrintWindow(selected: Set<string>): void {
        const req = this.data.request;
        const esc = (s: string) => this.esc(s);
        const nl2br = (s: string) => this.nl2br(s);
        const fd = (d: Date | string) => this.formatDate(d);
        const fdt = (d: Date | string) => this.formatDateTime(d);

        const currentStatus = req.category?.statuses?.find((s) => s._id === req.state);
        const statusColor = currentStatus?.color || '#6b7280';

        const statusName = esc(currentStatus?.name || req.state || '');
        const categoryName = esc(req.category?.name || '');
        const deptName = esc(req.department?.name || '');
        const printTitle = `${this.t('request_v2.print.title')} ${req.requestId}`;

        let body = '';
        const includesMap = selected.has('map') && req.location?.geometry?.coordinates?.length >= 2;

        const sectionHeader = (label: string) => `<div class="section-header"><h2 class="section-title">${label}</h2></div>`;

        // ---- Info section (card: breadcrumb + status + dates) ----
        if (selected.has('info')) {
            const subTitle = req.subCategory?.name || '';
            const subHtml = subTitle ? `<div class="crumb-row">└ ${esc(subTitle)}</div>` : '';
            const deptHtml2 = deptName ? `<div class="crumb-dept">└ ${deptName}</div>` : '';
            const createdAtLabel = esc(this.t('request_v2.print.field.created_at'));
            const slaLabel = esc(this.t('request_v2.print.field.sla'));
            const statusHtml = statusName
                ? `<div class="info-status-pill" style="background:${statusColor}22;border:1px solid ${statusColor}66;color:${statusColor};"><span class="info-status-dot" style="background:${statusColor};"></span>${statusName}</div>`
                : '';
            body += `
                <section class="print-section">
                    <div class="info-card">
                        <div class="info-card-left">
                            <div class="crumb-main">${categoryName}</div>
                            ${subHtml}
                            ${deptHtml2}
                        </div>
                        <div class="info-card-right">
                            ${statusHtml}
                            ${req.createdAt ? `<div class="info-date"><span class="info-date-label">${createdAtLabel}: </span>${esc(fd(req.createdAt))}</div>` : ''}
                            ${req.sla ? `<div class="info-date"><span class="info-date-label">${slaLabel}: </span>${esc(fd(req.sla))}</div>` : ''}
                        </div>
                    </div>
                </section>`;
        }

        // ---- Description section (avant demandeur/emplacement) ----
        if (selected.has('description') && req.description) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.description')))}
                    <p class="body-text">${nl2br(req.description)}</p>
                </section>`;
        }

        // ---- Requester + Address side by side ----
        let requesterInner = '';
        if (selected.has('requester') && req.user) {
            const firstName = req.user.firstName || '';
            const lastName = req.user.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const email = req.user.email || '';
            const phone = this.requesterPhone;
            const picture = req.user.picture || '';
            const initials = this.getInitials(firstName, lastName);
            const avatarHtml = picture ? `<img src="${esc(picture)}" alt="${esc(fullName)}">` : `${esc(initials)}`;
            requesterInner = `
                <div class="col-label">${esc(this.t('request_v2.print.section.requester'))}</div>
                <div class="requester-row">
                    <div class="user-avatar">${avatarHtml}</div>
                    <span class="user-name">${esc(fullName)}</span>
                </div>
                <div class="contact-rows">
                    ${phone ? `<div class="contact-row"><span class="material-icons contact-icon">phone</span><span class="contact-text">${esc(phone)}</span></div>` : ''}
                    ${email ? `<div class="contact-row"><span class="material-icons contact-icon">email</span><span class="contact-text">${esc(email)}</span></div>` : ''}
                </div>`;
        }

        let addressInner = '';
        if (selected.has('address')) {
            const line1 = this.formatAddressLine1(req.location);
            const line2 = this.formatAddressLine2(req.location);
            const fallback = req.location?.address?.formattedAddress || '';
            const displayLine1 = line1 || fallback;
            if (displayLine1) {
                addressInner = `
                    <div class="col-label">${esc(this.t('request_v2.print.section.address'))}</div>
                    <div class="address-icon-row">
                        <span class="material-icons address-icon">place</span>
                        <div class="address-lines">
                            <span class="address-line">${esc(displayLine1)}</span>
                            ${line2 ? `<span class="address-line address-line-city">${esc(line2)}</span>` : ''}
                            ${req.location?.entireStreet ? `<span class="entire-street-chip">${esc(this.t('request_v2.print.entire_street'))}</span>` : ''}
                        </div>
                    </div>`;
            }
        }

        if (requesterInner || addressInner) {
            body += `
                <section class="print-section">
                    <div class="citizen-row">
                        ${requesterInner ? `<div class="citizen-col">${requesterInner}</div>` : ''}
                        ${addressInner ? `<div class="citizen-col">${addressInner}</div>` : ''}
                    </div>
                </section>`;
        }

        // ---- Map section (placeholder div — Leaflet inits in <script>) ----
        if (includesMap) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.map')))}
                    <div id="print-map"></div>
                </section>`;
        }

        // ---- Messages section ----
        if (selected.has('messages')) {
            const msgs = (req.messages || []).filter((m) => !m.deleted);
            if (msgs.length) {
                body += `
                    <section class="print-section">
                        ${sectionHeader(esc(this.t('request_v2.print.section.messages')))}
                        <div class="messages-list">
                        ${msgs
                            .map((m) => {
                                const sender = `${m.sender?.firstName || ''} ${m.sender?.lastName || ''}`.trim();
                                const badge = m.isAdmin ? this.t('request_v2.print.admin') : this.t('request_v2.print.citizen');
                                return `<div class="message-item ${m.isAdmin ? 'message-admin' : 'message-citizen'}">
                                    <div class="message-meta">
                                        <span class="message-sender">${esc(sender)}</span>
                                        <span class="message-badge">${esc(badge)}</span>
                                        <span class="message-date">${esc(fdt(m.sentAt))}</span>
                                    </div>
                                    <p class="message-text">${nl2br(m.message)}</p>
                                </div>`;
                            })
                            .join('')}
                        </div>
                    </section>`;
            }
        }

        // ---- Tasks section ----
        if (selected.has('tasks') && req.subtasks?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.tasks')))}
                    <ul class="tasks-list">
                        ${req.subtasks
                            .map((task) => {
                                const assignee = task.assignedTo ? `${esc(task.assignedTo.firstName)} ${esc((task.assignedTo.lastName || '').charAt(0).toUpperCase())}.` : '';
                                return `<li class="task-item${task.isDone ? ' task-done' : ''}">
                                <span class="task-check">${task.isDone ? '&#9745;' : '&#9744;'}</span>
                                <span class="task-desc">${esc(task.description)}</span>
                                ${assignee ? `<span class="task-assignee">${assignee}</span>` : ''}
                                ${task.dueDate ? `<span class="task-due"><span class="task-date-label">${esc(this.t('request_v2.print.field.sla'))}</span> ${esc(fd(task.dueDate))}</span>` : ''}
                                ${task.completedAt ? `<span class="task-completed"><span class="task-date-label">${esc(this.t('request_v2.print.field.completed_at'))}</span> ${esc(fd(task.completedAt))}</span>` : ''}
                            </li>`;
                            })
                            .join('')}
                    </ul>
                </section>`;
        }

        // ---- Linked requests section ----
        if (selected.has('linked') && req.links?.length) {
            const linkLabels: Record<string, string> = {
                BLOCKS: this.t('request_v2.links.types.BLOCKS'),
                IS_BLOCKED_BY: this.t('request_v2.links.types.IS_BLOCKED_BY'),
                IS_DUPPLICATE_OF: this.t('request_v2.links.types.IS_DUPPLICATE_OF'),
                IS_LINKED_TO: this.t('request_v2.links.types.IS_LINKED_TO'),
            };
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.linked')))}
                    <ul class="links-list">
                        ${req.links
                            .map((link) => {
                                const linked = link.linkedRequest;
                                const typeLabel = linkLabels[link.type] || link.type;
                                const name = linked?.subCategory?.name || linked?.category?.name || '';
                                return `<li class="link-item">
                                    <span class="link-type">${esc(typeLabel)}</span>
                                    <span class="link-id">${esc(linked?.requestId || '')}</span>
                                    ${name ? `<span class="link-name">${esc(name)}</span>` : ''}
                                </li>`;
                            })
                            .join('')}
                    </ul>
                </section>`;
        }

        // ---- Tags section ----
        if (selected.has('tags') && req.tags?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.tags')))}
                    <div class="tags-list">
                        ${req.tags.map((tag) => `<span class="tag">${esc(tag)}</span>`).join('')}
                    </div>
                </section>`;
        }

        // ---- Internal files section ----
        if (selected.has('internal_files') && req.internalFiles?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.internal_files')))}
                    <ul class="files-list">
                        ${req.internalFiles.map((f) => `<li>${esc(f.originalName)}</li>`).join('')}
                    </ul>
                </section>`;
        }

        // ---- Internal description section ----
        if (selected.has('internal_description') && req.internalDescription) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.internal_description')))}
                    <p class="body-text">${nl2br(req.internalDescription)}</p>
                </section>`;
        }

        // ---- Notes section ----
        if (selected.has('notes') && req.internalNotes?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.notes')))}
                    <div class="notes-list">
                    ${req.internalNotes
                        .map((note) => {
                            const author = `${note.user?.firstName || ''} ${note.user?.lastName || ''}`.trim();
                            return `<div class="note-item">
                                <div class="note-meta">
                                    <span class="note-author">${esc(author)}</span>
                                    <span class="note-date">${esc(fdt(note.date))}</span>
                                    ${note.edited ? `<span class="note-edited">(${esc(this.t('request_v2.print.edited'))})</span>` : ''}
                                </div>
                                <p class="note-text">${nl2br(note.description)}</p>
                            </div>`;
                        })
                        .join('')}
                    </div>
                </section>`;
        }

        // ---- History section ----
        if (selected.has('history') && req.history?.length) {
            const visible = req.history;
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('request_v2.print.section.history')))}
                    <div class="history-list">
                    ${visible
                        .map((h) => {
                            const user = `${h.user?.firstName || ''} ${h.user?.lastName || ''}`.trim();
                            return `<div class="history-item">
                                <span class="history-date">${esc(fdt(h.date))}</span>
                                <span class="history-user">${esc(user)}</span>
                                <span class="history-action">${esc(h.message || h.actionKey || '')}</span>
                            </div>`;
                        })
                        .join('')}
                    </div>
                </section>`;
        }

        // ---- Photos section (last, so the field team gets them in one block) ----
        // Citizen and internal photos print under their own sub-heading, like in the dialog
        const photoGroupsHtml = selected.has('photos')
            ? this.photoGroups
                  .map((group) => ({ label: group.label, photos: group.photos.filter((p) => p.selected) }))
                  .filter((group) => group.photos.length)
                  .map(
                      (group) => `
                    <div class="photos-group">
                        <h3 class="photos-group-title">${esc(this.t(group.label))}</h3>
                        <div class="photos-grid">
                            ${group.photos
                                .map(
                                    (p) => `<figure class="photo">
                                    <img src="${esc(p.url)}" alt="${esc(p.name)}" onerror="this.parentNode.remove()">
                                    <figcaption>${esc(p.name)}</figcaption>
                                </figure>`,
                                )
                                .join('')}
                        </div>
                    </div>`,
                  )
                  .join('')
            : '';
        if (photoGroupsHtml) {
            body += `
                <section class="print-section print-section-photos">
                    ${sectionHeader(esc(this.t('request_v2.print.section.photos')))}
                    ${photoGroupsHtml}
                </section>`;
        }

        const generatedOn = `${this.t('request_v2.print.generated_on')} ${fdt(new Date().toISOString())}`;

        // Map coordinates (GeoJSON: [lng, lat])
        const coords = req.location?.geometry?.coordinates;
        const mapLat = includesMap ? coords[1] : 0;
        const mapLng = includesMap ? coords[0] : 0;

        const mapScript = includesMap
            ? `
            var map = L.map('print-map', { zoomControl: false, attributionControl: false });
            map.setView([${mapLat}, ${mapLng}], 15);
            L.tileLayer('${MAP_TILE_URL}', { maxZoom: 19, attribution: '${MAP_TILE_ATTRIBUTION}' }).addTo(map);
            var pinSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 26 33"><path d="M13 1C8.03 1 4 5.01 4 10.13c0 4.62 4.02 10.55 9 13.31C18 20.68 22 14.75 22 10.13 22 5.01 17.97 1 13 1z" fill="${statusColor}" stroke="white" stroke-width="1.5"/></svg>';
            var icon = L.divIcon({ html: pinSvg, className: '', iconSize: [24,32], iconAnchor: [12,32] });
            L.marker([${mapLat}, ${mapLng}], { icon: icon }).addTo(map);
            setTimeout(function() { map.invalidateSize(); doPrint(); }, 1200);
        `
            : 'doPrint();';

        const leafletHead = `
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
            ${
                includesMap
                    ? `<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
            <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>`
                    : ''
            }
        `;

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(printTitle)}</title>
${leafletHead}
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; color: #111827; background: #fff; }

  .page-header { background: #f9fafb; border-bottom: 1px solid #e5e7eb; padding: 12px 32px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .page-request-id { font-size: 12px; font-weight: 700; color: #374151; }
  .page-meta { font-size: 10px; color: #9ca3af; }

  .page-content { padding: 28px 32px; }

  .print-section { margin-bottom: 26px; page-break-inside: avoid; }
  .print-section-photos { page-break-inside: auto; }
  .section-header { display: flex; align-items: center; margin-bottom: 10px; }
  h2.section-title { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.1em; }

  /* Requester — copie exacte de request-info.component.scss */
  .requester-row { display: flex; align-items: center; gap: 8px; }
  .user-avatar { width: 26px; height: 26px; border-radius: 50%; background-color: #e5e7eb; color: #374151; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; flex-shrink: 0; overflow: hidden; }
  .user-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .user-name { font-size: 14px; font-weight: 600; color: #111827; text-decoration: none; }
  .contact-rows { display: flex; flex-direction: column; gap: 5px; padding-left: 34px; margin-top: 8px; }
  .contact-row { display: flex; align-items: center; gap: 7px; }
  .contact-icon { font-size: 14px; width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; vertical-align: middle; }
  .contact-text { font-size: 13px; color: #6b7280; }

  /* Address — copie exacte de request-info.component.scss */
  .address-icon-row { display: flex; align-items: flex-start; gap: 6px; }
  .address-icon { font-size: 14px; width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; margin-top: 2px; vertical-align: middle; }
  .address-lines { display: flex; flex-direction: column; gap: 1px; }
  .address-line { font-size: 13px; color: #374151; line-height: 1.4; display: block; }
  .address-line-city { color: #6b7280; }
  .entire-street-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px; background-color: #FFF8E1; border: 1px solid #FFC107; border-radius: 4px; color: #B45309; font-size: 12px; font-weight: 500; width: fit-content; margin-top: 8px; }

  /* Map */
  #print-map { width: 100%; height: 220px; border-radius: 10px; border: 1px solid #e5e7eb; overflow: hidden; }

  .body-text { font-size: 12px; color: #374151; line-height: 1.7; background: #f9fafb; border-radius: 8px; padding: 12px 14px; border-left: 3px solid #e5e7eb; }

  .messages-list { display: flex; flex-direction: column; gap: 8px; }
  .message-item { padding: 10px 12px; border-radius: 8px; border-left: 3px solid #e5e7eb; }
  .message-admin { border-left-color: #6366f1; background: #f5f3ff; }
  .message-citizen { border-left-color: #10b981; background: #f0fdf4; }
  .message-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
  .message-sender { font-weight: 700; color: #111827; font-size: 12px; }
  .message-badge { padding: 1px 7px; border-radius: 4px; background: rgba(0,0,0,0.06); color: #6b7280; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .message-date { color: #9ca3af; font-size: 10px; margin-left: auto; }
  .message-text { font-size: 12px; color: #374151; line-height: 1.6; }

  .tasks-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .task-item { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .task-check { font-size: 14px; flex-shrink: 0; color: #374151; }
  .task-desc { flex: 1; color: #111827; }
  .task-done .task-desc { text-decoration: line-through; color: #9ca3af; }
  .task-assignee { font-size: 11px; font-weight: 600; color: #374151; flex-shrink: 0; }
  .task-due { font-size: 10px; color: #9ca3af; background: #f3f4f6; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .task-completed { font-size: 10px; color: #059669; background: #ecfdf5; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .task-date-label { font-weight: 600; opacity: 0.75; }

  .links-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .link-item { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .link-type { font-weight: 600; color: #6b7280; width: 90px; flex-shrink: 0; font-size: 11px; }
  .link-id { font-weight: 700; font-size: 11px; background: #e5e7eb; border-radius: 5px; padding: 2px 8px; color: #374151; flex-shrink: 0; }
  .link-name { color: #374151; flex: 1; }

  .tags-list { display: flex; flex-wrap: wrap; gap: 6px; }
  .tag { padding: 4px 12px; background: #f3f4f6; border-radius: 20px; font-size: 11px; font-weight: 500; color: #374151; border: 1px solid #e5e7eb; }

  .files-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .files-list li { padding: 7px 10px; font-size: 12px; color: #374151; background: #f9fafb; border-radius: 6px; border: 1px solid #f3f4f6; display: flex; align-items: center; gap: 8px; }
  .files-list li::before { content: "📎"; font-size: 13px; }

  .photos-group + .photos-group { margin-top: 18px; padding-top: 14px; border-top: 1px solid #e5e7eb; }
  .photos-group-title { font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 8px; }
  .photos-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .photo { break-inside: avoid; page-break-inside: avoid; border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px; }
  .photo img { display: block; width: 100%; max-height: 300px; object-fit: contain; border-radius: 4px; }
  .photo figcaption { font-size: 10px; color: #6b7280; margin-top: 4px; word-break: break-all; }

  .notes-list { display: flex; flex-direction: column; gap: 8px; }
  .note-item { padding: 10px 12px; background: #f9fafb; border-radius: 8px; border: 1px solid #f3f4f6; border-left: 3px solid #d1d5db; }
  .note-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .note-author { font-weight: 700; color: #111827; font-size: 12px; }
  .note-date { color: #9ca3af; font-size: 10px; }
  .note-edited { font-style: italic; color: #9ca3af; font-size: 10px; }
  .note-text { font-size: 12px; color: #374151; line-height: 1.6; }

  .history-list { display: flex; flex-direction: column; }
  .history-item { display: flex; gap: 12px; align-items: baseline; padding: 6px 0; font-size: 11px; border-bottom: 1px solid #f3f4f6; }
  .history-item:last-child { border-bottom: none; }
  .history-date { color: #9ca3af; width: 115px; flex-shrink: 0; }
  .history-user { font-weight: 600; color: #374151; width: 135px; flex-shrink: 0; }
  .history-action { color: #6b7280; flex: 1; }

  /* Info card */
  .info-card { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  .info-card-left { flex: 1; }
  .crumb-main { font-size: 15px; font-weight: 700; color: #111827; }
  .crumb-row { font-size: 12px; color: #6b7280; margin-top: 3px; }
  .crumb-dept { font-size: 11px; color: #9ca3af; margin-top: 2px; }
  .info-card-right { display: flex; flex-direction: column; align-items: flex-end; gap: 5px; flex-shrink: 0; }
  .info-status-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
  .info-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .info-date { font-size: 10px; color: #6b7280; }
  .info-date-label { color: #9ca3af; }

  /* Citizen row - demandeur + emplacement côte à côte */
  .citizen-row { display: flex; gap: 28px; align-items: flex-start; }
  .citizen-col { flex: 1; min-width: 0; }
  .col-label { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }

  @media print {
    .page-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .avatar-img, .avatar-initials { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    #print-map { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-section { page-break-inside: avoid; break-inside: avoid; }
    /* A photo block can be taller than a page; only keep each photo whole */
    .print-section-photos { page-break-inside: auto; break-inside: auto; }
  }
</style>
</head>
<body>
  <div class="page-header">
    <div class="page-request-id">${esc(this.t('request_v2.print.field.request_id'))} ${esc(req.requestId)}</div>
    <div class="page-meta">${esc(generatedOn)}</div>
  </div>

  <div class="page-content">
    ${body}
  </div>

  <script>
    function doPrint() {
      window.print();
      window.onafterprint = function() { window.close(); };
    }
    window.onload = function() {
      ${mapScript}
    };
  <\/script>
</body>
</html>`;

        const win = window.open('', '_blank', 'width=860,height=700');
        if (!win) return;
        win.document.write(html);
        win.document.close();
    }
}
